import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import {
    UserPlus, Clock, Layers, CheckCircle, XCircle, Calendar, Edit,
} from "lucide-react";
import api from "../api";
import { getShiftColor, buildShiftColorMap } from "../utils/shiftColors";
import DutyRoster from "./DutyRoster";

// ─── Styled Components ────────────────────────────────────────────────────────

const Container = styled.div`
  padding: 24px 28px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 16px 12px;
  }

  @media (max-width: 480px) {
    padding: 12px 8px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;

  @media (max-width: 480px) {
    margin-bottom: 18px;
  }
`;

const Title = styled.h1`
  font-size: clamp(18px, 4vw, 28px);
  font-weight: 700;
  color: #e2e8f0;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Card = styled.div`
  background: rgba(30, 41, 59, 0.7);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    border-radius: 14px;
    margin-bottom: 20px;
  }
`;

// ── Table ──────────────────────────────────────────────────────────────────────

const TableScrollWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: auto;
  max-height: ${p => p.$maxHeight || 'auto'};
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(15, 23, 42, 0.3);
    border-radius: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.4);
    border-radius: 8px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 520px;
`;

const Th = styled.th`
  text-align: left;
  padding: 16px 20px;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  white-space: nowrap;
  position: sticky;
  top: 0;
  background: #1e293b;
  z-index: 10;

  @media (max-width: 768px) {
    padding: 12px 14px;
    font-size: 11px;
  }
`;

const Td = styled.td`
  padding: 16px 20px;
  color: #e2e8f0;
  font-size: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  vertical-align: middle;

  @media (max-width: 768px) {
    padding: 12px 14px;
    font-size: 13px;
  }
`;

const Tr = styled.tr`
  transition: all 0.2s ease;
  background: ${(p) => (p.$disabled ? "rgba(239, 68, 68, 0.05)" : "transparent")};
  opacity: ${(p) => (p.$disabled ? 0.7 : 1)};

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const Badge = styled.span`
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) => p.color || "rgba(99, 102, 241, 0.1)"};
  color: ${(p) => p.textColor || "#818cf8"};
  border: 1px solid ${(p) => p.borderColor || "rgba(99, 102, 241, 0.2)"};
  white-space: nowrap;
`;

// ── Form ───────────────────────────────────────────────────────────────────────

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  background: rgba(30, 41, 59, 0.4);
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  @media (max-width: 768px) {
    padding: 14px;
    gap: 14px;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 14px;
  align-items: flex-end;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  flex: 1;
  min-width: 160px;

  @media (max-width: 640px) {
    min-width: 100%;
  }
`;

const FormLabel = styled.label`
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
  margin-left: 3px;
`;

const ChipGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  padding: 14px;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  max-height: 200px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.2);
    border-radius: 4px;
  }
`;

const ShiftChip = styled.div`
  padding: 7px 14px;
  border-radius: 100px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 7px;
  background: ${(p) => (p.selected ? "rgba(99, 102, 241, 0.2)" : "rgba(255,255,255,0.03)")};
  color: ${(p) => (p.selected ? "#818cf8" : "#64748b")};
  border: 1px solid ${(p) => (p.selected ? "rgba(99, 102, 241, 0.4)" : "transparent")};

  &:hover {
    background: ${(p) => (p.selected ? "rgba(99, 102, 241, 0.25)" : "rgba(255,255,255,0.08)")};
    color: ${(p) => (p.selected ? "#a5b4fc" : "#94a3b8")};
    transform: translateY(-1px);
  }

  @media (max-width: 480px) {
    font-size: 11px;
    padding: 6px 10px;
  }
`;

const InputGroup = styled.div`
  display: flex;
  gap: 10px;
  padding: 18px 20px;
  background: rgba(15, 23, 42, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  flex-wrap: wrap;
  align-items: center;

  @media (max-width: 768px) {
    padding: 14px;
    gap: 10px;
  }
`;

const Input = styled.input`
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #f1f5f9;
  padding: 10px 14px;
  border-radius: 11px;
  font-size: 13px;
  outline: none;
  transition: all 0.2s;
  flex: 1;
  min-width: 160px;

  &:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
  }

  &[type="time"] {
    min-width: 130px;
    &::-webkit-calendar-picker-indicator {
      filter: invert(1);
      cursor: pointer;
    }
  }

  @media (max-width: 640px) {
    min-width: 100%;
    width: 100%;
  }
`;

const Button = styled.button`
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 11px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 640px) {
    width: 100%;
    justify-content: center;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;

  @media (max-width: 640px) {
    flex-direction: column;
    width: 100%;
  }
`;

const ToggleButton = styled.button`
  background: ${(p) => (p.active ? "rgba(16, 185, 129, 0.2)" : "rgba(239, 68, 68, 0.2)")};
  color: ${(p) => (p.active ? "#34d399" : "#f87171")};
  border: 1px solid ${(p) => (p.active ? "rgba(16, 185, 129, 0.3)" : "rgba(239, 68, 68, 0.3)")};
  padding: 5px 11px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${(p) => (p.active ? "rgba(16, 185, 129, 0.3)" : "rgba(239, 68, 68, 0.3)")};
  }
`;

const StyledSelect = styled.select`
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #f1f5f9;
  padding: 9px 36px 9px 14px;
  border-radius: 11px;
  font-size: 13px;
  outline: none;
  transition: all 0.2s;
  cursor: pointer;
  min-width: 160px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 15px;

  &:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
  }

  option {
    background: #1e293b;
    color: #f1f5f9;
  }

  @media (max-width: 640px) {
    width: 100%;
  }
`;

// ── Tabs ───────────────────────────────────────────────────────────────────────

const ModernTabContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  background: rgba(15, 23, 42, 0.6);
  padding: 6px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  margin-bottom: 28px;
  backdrop-filter: blur(10px);
  gap: 6px;

  @media (max-width: 480px) {
    border-radius: 12px;
    padding: 4px;
    gap: 4px;
    margin-bottom: 18px;
  }
`;

const ModernTab = styled.button`
  background: ${(p) => (p.active ? "rgba(99, 102, 241, 0.2)" : "transparent")};
  color: ${(p) => (p.active ? "#818cf8" : "#94a3b8")};
  border: 1px solid ${(p) => (p.active ? "rgba(99, 102, 241, 0.1)" : "transparent")};
  padding: 9px 18px;
  border-radius: 11px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  white-space: nowrap;
  flex: 1;

  &:hover {
    color: #e2e8f0;
    background: ${(p) => (p.active ? "rgba(99, 102, 241, 0.25)" : "rgba(255, 255, 255, 0.04)")};
  }

  @media (max-width: 480px) {
    padding: 8px 10px;
    font-size: 11px;
    gap: 4px;

    svg { width: 14px; height: 14px; }
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #e2e8f0;
  font-size: 13px;
  cursor: pointer;

  input {
    width: 15px;
    height: 15px;
    accent-color: #6366f1;
  }
`;

const HRbaseurl = process.env.REACT_APP_BACKEND_HR_BASE_URL;

// ─── ShiftManagement Component ────────────────────────────────────────────────

const ShiftManagement = () => {
    const [activeTab, setActiveTab] = useState("shifts");
    const [shifts, setShifts] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [globalDepartments, setGlobalDepartments] = useState([]);
    const [newShift, setNewShift] = useState({ name: "", start_time: "", end_time: "", is_active: true });
    const [editingShift, setEditingShift] = useState(null);
    const [newDept, setNewDept] = useState({ name: "", shift_ids: [] });
    const [editingDept, setEditingDept] = useState(null);
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [newLeaveType, setNewLeaveType] = useState({ name: "", is_active: true });
    const [editingLeaveType, setEditingLeaveType] = useState(null);

    const role = localStorage.getItem("role");
    const userDept = localStorage.getItem("department_id");
    const isRestricted = role && !["Admin", "admin"].includes(role) && userDept;

    const fetchAll = async () => {
        try {
            let url = `${HRbaseurl}shifts/`;
            if (isRestricted) url += `?department/`;
            const shiftRes = await api.get(url.replace(HRbaseurl, ""));
            setShifts(shiftRes.data);

            const deptRes = await api.get("departments/");
            setDepartments(deptRes.data);

            try {
                const globalRes = await api.get("global-departments/");
                let gData = Array.isArray(globalRes.data) ? globalRes.data : [];
                const deptCodes = localStorage.getItem("department");
                if (isRestricted) {
                    const assignedDepts = userDept ? userDept.split(",").map((d) => d.trim()) : [];
                    const assignedCodes = deptCodes ? deptCodes.split(",").map((d) => d.trim()) : [];
                    gData = gData.filter(
                        (d) => assignedDepts.includes(d.id?.toString()) || assignedCodes.includes(d.department_code)
                    );
                }
                setGlobalDepartments(gData);
            } catch (err) {
                console.error("Error fetching global departments", err);
            }

            if (role === "Admin") {
                try {
                    const leaveTypeRes = await api.get("leave-types/");
                    setLeaveTypes(leaveTypeRes.data);
                } catch (err) {
                    console.error("Error fetching leave types", err);
                }
            }
        } catch (error) {
            console.error("Error fetching admin data", error);
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const handleCreateShift = async () => {
        if (!newShift.name || !newShift.start_time || !newShift.end_time) { alert("Please fill all fields"); return; }
        try { await api.post("shifts/", newShift); setNewShift({ name: "", start_time: "", end_time: "", is_active: true }); fetchAll(); }
        catch { alert("Error creating shift. Name must be unique."); }
    };

    const handleUpdateShift = async () => {
        if (!newShift.name || !newShift.start_time || !newShift.end_time) { alert("Please fill all fields"); return; }
        try { await api.put(`shifts/${editingShift.id}/`, newShift); setNewShift({ name: "", start_time: "", end_time: "", is_active: true }); setEditingShift(null); fetchAll(); }
        catch { alert("Error updating shift."); }
    };

    const handleEditShift = (shift) => {
        setEditingShift(shift);
        setNewShift({ name: shift.name, start_time: shift.start_time, end_time: shift.end_time, is_active: shift.is_active });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleToggleShift = async (shift) => {
        try { await api.put(`shifts/${shift.id}/`, { ...shift, is_active: !shift.is_active }); fetchAll(); }
        catch { alert("Failed to update status"); }
    };

    const handleCreateLeaveType = async () => {
        if (!newLeaveType.name) { alert("Please enter a leave type name"); return; }
        try { await api.post("leave-types/", newLeaveType); setNewLeaveType({ name: "", is_active: true }); fetchAll(); }
        catch { alert("Error creating leave type. Name must be unique."); }
    };

    const handleUpdateLeaveType = async () => {
        if (!newLeaveType.name) { alert("Please enter a leave type name"); return; }
        try { await api.put(`leave-types/${editingLeaveType.id}/`, newLeaveType); setNewLeaveType({ name: "", is_active: true }); setEditingLeaveType(null); fetchAll(); }
        catch { alert("Error updating leave type."); }
    };

    const handleEditLeaveType = (leaveType) => {
        setEditingLeaveType(leaveType);
        setNewLeaveType({ name: leaveType.name, is_active: leaveType.is_active });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleToggleLeaveType = async (leaveType) => {
        try { await api.put(`leave-types/${leaveType.id}/`, { ...leaveType, is_active: !leaveType.is_active }); fetchAll(); }
        catch { alert("Failed to update status"); }
    };

    const handleCreateDept = async () => {
        if (!newDept.name) { alert("Please enter department name"); return; }
        try { await api.post("departments/", newDept); setNewDept({ name: "", shift_ids: [] }); fetchAll(); }
        catch { alert("Error creating department."); }
    };

    const handleUpdateDept = async () => {
        if (!newDept.name) { alert("Please enter department name"); return; }
        try { await api.put(`departments/${editingDept.id}/`, newDept); setNewDept({ name: "", shift_ids: [] }); setEditingDept(null); fetchAll(); }
        catch { alert("Error updating department."); }
    };

    const handleEditDept = (dept) => {
        setEditingDept(dept);
        setNewDept({ name: dept.name, shift_ids: dept.shifts.map((s) => s.id) });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const toggleShiftSelection = (id) =>
        setNewDept((prev) => ({
            ...prev,
            shift_ids: prev.shift_ids.includes(id)
                ? prev.shift_ids.filter((sid) => sid !== id)
                : [...prev.shift_ids, id],
        }));

    const shiftColorMap = useMemo(() => buildShiftColorMap(shifts), [shifts]);

    const calculateDuration = (start, end) => {
        if (!start || !end) return "--";
        const parse = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
        let s = parse(start), e = parse(end);
        if (e < s) e += 24 * 60;
        const diff = e - s;
        const h = Math.floor(diff / 60), m = diff % 60;
        return h > 0 && m > 0 ? `${h}h ${m}m` : h > 0 ? `${h}h` : `${m}m`;
    };

    // ─── Render ─────────────────────────────────────────────────────────────────

    return (
        <Container>
            <PageHeader>
                <Title>
                    <Clock size={28} color="#818cf8" />
                    Shift Management
                </Title>
            </PageHeader>

            {/* ── Tabs ── */}
            <ModernTabContainer>
                <ModernTab active={activeTab === "shifts"} onClick={() => setActiveTab("shifts")}>
                    <Layers size={16} /> Shift Config
                </ModernTab>
                <ModernTab active={activeTab === "departments"} onClick={() => setActiveTab("departments")}>
                    <Layers size={16} /> Departments
                </ModernTab>
                <ModernTab active={activeTab === "roster"} onClick={() => setActiveTab("roster")}>
                    <Calendar size={16} /> Duty Roster
                </ModernTab>
                {role === "Admin" && (
                    <ModernTab active={activeTab === "leaveTypes"} onClick={() => setActiveTab("leaveTypes")}>
                        <Clock size={16} /> Leave Management
                    </ModernTab>
                )}
            </ModernTabContainer>

            {/* ── Shifts Tab ── */}
            {activeTab === "shifts" && (
                <Card>
                    {role === "Admin" && (
                        <InputGroup>
                            <Input
                                placeholder="Shift Name (e.g. A, B)"
                                value={newShift.name}
                                onChange={(e) => setNewShift({ ...newShift, name: e.target.value })}
                            />
                            <Input
                                type="time"
                                value={newShift.start_time}
                                onChange={(e) => setNewShift({ ...newShift, start_time: e.target.value })}
                            />
                            <span style={{ color: "#94a3b8", whiteSpace: "nowrap" }}>to</span>
                            <Input
                                type="time"
                                value={newShift.end_time}
                                onChange={(e) => setNewShift({ ...newShift, end_time: e.target.value })}
                            />
                            <CheckboxLabel>
                                <input
                                    type="checkbox"
                                    checked={newShift.is_active}
                                    onChange={(e) => setNewShift({ ...newShift, is_active: e.target.checked })}
                                />
                                Active
                            </CheckboxLabel>
                            <ButtonGroup>
                                {editingShift ? (
                                    <>
                                        <Button onClick={handleUpdateShift}><Edit size={16} /> Update Shift</Button>
                                        <Button onClick={() => { setNewShift({ name: "", start_time: "", end_time: "", is_active: true }); setEditingShift(null); }}
                                            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.2)" }}>
                                            Cancel
                                        </Button>
                                    </>
                                ) : (
                                    <Button onClick={handleCreateShift}><UserPlus size={16} /> Add Shift</Button>
                                )}
                            </ButtonGroup>
                        </InputGroup>
                    )}

                    <TableScrollWrapper $maxHeight="450px">
                        <Table>
                            <thead>
                                <Tr>
                                    <Th>S.No</Th>
                                    <Th>Timings</Th>
                                    <Th>Shift</Th>
                                    <Th>Duration</Th>
                                    <Th>Status</Th>
                                    {role === "Admin" && <Th>Actions</Th>}
                                </Tr>
                            </thead>
                            <tbody>
                                {shifts.map((shift, idx) => {
                                    const style = getShiftColor(idx);
                                    const duration = calculateDuration(shift.start_time, shift.end_time);
                                    return (
                                        <Tr key={shift.id} $disabled={!shift.is_active}>
                                            <Td>{idx + 1}</Td>
                                            <Td>
                                                <Badge color={style.bg} textColor={style.text} borderColor={style.border}>
                                                    {shift.start_time} – {shift.end_time}
                                                </Badge>
                                            </Td>
                                            <Td style={{ fontWeight: 700 }}>{shift.name}</Td>
                                            <Td style={{ fontFamily: "monospace", color: "#94a3b8" }}>{duration}</Td>
                                            <Td>
                                                <ToggleButton
                                                    active={shift.is_active}
                                                    onClick={() => role === "Admin" && handleToggleShift(shift)}
                                                    style={{ opacity: role === "Admin" ? 1 : 0.5, cursor: role === "Admin" ? "pointer" : "not-allowed" }}
                                                >
                                                    {shift.is_active ? <CheckCircle size={13} /> : <XCircle size={13} />}
                                                    {shift.is_active ? "Enabled" : "Disabled"}
                                                </ToggleButton>
                                            </Td>
                                            {role === "Admin" && (
                                                <Td>
                                                    <ToggleButton active onClick={() => handleEditShift(shift)}
                                                        style={{ padding: "5px 11px", borderRadius: 8, background: "rgba(99,102,241,0.1)", color: "#818cf8", border: "1px solid rgba(99,102,241,0.2)" }}>
                                                        <Edit size={13} /> Edit
                                                    </ToggleButton>
                                                </Td>
                                            )}
                                        </Tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </TableScrollWrapper>
                </Card>
            )}

            {/* ── Departments Tab ── */}
            {activeTab === "departments" && (
                <Card>
                    {role === "Admin" && (
                        <FormContainer>
                            <FormRow>
                                <FormField>
                                    <FormLabel>Department Name</FormLabel>
                                    <StyledSelect
                                        value={newDept.name}
                                        onChange={(e) => setNewDept({ ...newDept, name: e.target.value })}
                                        disabled={!!editingDept}
                                    >
                                        <option value="">Select Global Department</option>
                                        {globalDepartments.map((d, i) => (
                                            <option key={i} value={d.department_name || d.name}>{d.department_name || d.name}</option>
                                        ))}
                                    </StyledSelect>
                                </FormField>
                                <ButtonGroup>
                                    {editingDept ? (
                                        <>
                                            <Button onClick={handleUpdateDept}><Layers size={16} /> Update Dept</Button>
                                            <Button onClick={() => { setNewDept({ name: "", shift_ids: [] }); setEditingDept(null); }}
                                                style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.2)" }}>
                                                Cancel
                                            </Button>
                                        </>
                                    ) : (
                                        <Button onClick={handleCreateDept}><Layers size={16} /> Create Dept</Button>
                                    )}
                                </ButtonGroup>
                            </FormRow>
                            <FormField>
                                <FormLabel>Allowed Shifts</FormLabel>
                                <ChipGrid>
                                    {shifts.filter((s) => s.is_active).map((shift) => (
                                        <ShiftChip
                                            key={shift.id}
                                            selected={newDept.shift_ids.includes(shift.id)}
                                            onClick={() => toggleShiftSelection(shift.id)}
                                        >
                                            {newDept.shift_ids.includes(shift.id) && <CheckCircle size={13} />}
                                            <span>{shift.name}</span>
                                            <span style={{ fontSize: 10, opacity: 0.6, borderLeft: "1px solid currentColor", paddingLeft: 6 }}>
                                                {shift.start_time?.slice(0, 5)} – {shift.end_time?.slice(0, 5)}
                                            </span>
                                        </ShiftChip>
                                    ))}
                                </ChipGrid>
                            </FormField>
                        </FormContainer>
                    )}

                    <TableScrollWrapper $maxHeight="450px">
                        <Table>
                            <thead>
                                <Tr>
                                    <Th>S.No</Th>
                                    <Th>Department</Th>
                                    <Th>Allowed Shifts</Th>
                                    <Th>Count</Th>
                                    {role === "Admin" && <Th>Actions</Th>}
                                </Tr>
                            </thead>
                            <tbody>
                                {globalDepartments.map((gDept, idx) => {
                                    const gName = gDept.department_name || gDept.name;
                                    const localDept = departments.find((d) => d.name === gName);
                                    return (
                                        <Tr key={idx}>
                                            <Td>{idx + 1}</Td>
                                            <Td>{gName}</Td>
                                            <Td>
                                                {localDept ? (
                                                    <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                                                        {localDept.shifts.map((shift, sIdx) => {
                                                            const s = shiftColorMap[shift.id] || getShiftColor(sIdx);
                                                            return <Badge key={shift.id} color={s.bg} textColor={s.text} borderColor={s.border}>{shift.name}</Badge>;
                                                        })}
                                                    </div>
                                                ) : (
                                                    <span style={{ color: "#64748b", fontSize: 12, fontStyle: "italic" }}>No shifts configured</span>
                                                )}
                                            </Td>
                                            <Td>{localDept ? localDept.shifts.length : 0}</Td>
                                            {role === "Admin" && (
                                                <Td>
                                                    <ToggleButton
                                                        active={!!localDept}
                                                        onClick={() => {
                                                            if (localDept) { handleEditDept(localDept); }
                                                            else { setNewDept({ name: gName, shift_ids: [] }); setEditingDept(null); window.scrollTo({ top: 0, behavior: "smooth" }); }
                                                        }}
                                                        style={{ padding: "5px 11px", borderRadius: 8, background: localDept ? "rgba(99,102,241,0.1)" : "rgba(148,163,184,0.1)", color: localDept ? "#818cf8" : "#94a3b8", border: `1px solid ${localDept ? "rgba(99,102,241,0.2)" : "rgba(148,163,184,0.2)"}` }}
                                                    >
                                                        {localDept ? <Edit size={13} /> : <Layers size={13} />}
                                                        {localDept ? "Edit" : "Configure"}
                                                    </ToggleButton>
                                                </Td>
                                            )}
                                        </Tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </TableScrollWrapper>
                </Card>
            )}

            {/* ── Leave Management Tab (Admin only) ── */}
            {activeTab === "leaveTypes" && role === "Admin" && (
                <Card>
                    <InputGroup>
                        <Input
                            placeholder="Leave Type Name (e.g. EL, COL, PH)"
                            value={newLeaveType.name}
                            onChange={(e) => setNewLeaveType({ ...newLeaveType, name: e.target.value })}
                        />
                        <CheckboxLabel>
                            <input
                                type="checkbox"
                                checked={newLeaveType.is_active}
                                onChange={(e) => setNewLeaveType({ ...newLeaveType, is_active: e.target.checked })}
                            />
                            Active
                        </CheckboxLabel>
                        <ButtonGroup>
                            {editingLeaveType ? (
                                <>
                                    <Button onClick={handleUpdateLeaveType}><Edit size={16} /> Update Leave Type</Button>
                                    <Button onClick={() => { setNewLeaveType({ name: "", is_active: true }); setEditingLeaveType(null); }}
                                        style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.2)" }}>
                                        Cancel
                                    </Button>
                                </>
                            ) : (
                                <Button onClick={handleCreateLeaveType}><UserPlus size={16} /> Add Leave Type</Button>
                            )}
                        </ButtonGroup>
                    </InputGroup>

                    <TableScrollWrapper $maxHeight="450px">
                        <Table>
                            <thead>
                                <Tr>
                                    <Th>S.No</Th>
                                    <Th>Leave Type</Th>
                                    <Th>Status</Th>
                                    <Th>Actions</Th>
                                </Tr>
                            </thead>
                            <tbody>
                                {leaveTypes.map((lt, idx) => (
                                    <Tr key={lt.id} $disabled={!lt.is_active}>
                                        <Td>{idx + 1}</Td>
                                        <Td style={{ fontWeight: 700 }}>{lt.name}</Td>
                                        <Td>
                                            <ToggleButton
                                                active={lt.is_active}
                                                onClick={() => handleToggleLeaveType(lt)}
                                            >
                                                {lt.is_active ? <CheckCircle size={13} /> : <XCircle size={13} />}
                                                {lt.is_active ? "Enabled" : "Disabled"}
                                            </ToggleButton>
                                        </Td>
                                        <Td>
                                            <ToggleButton active onClick={() => handleEditLeaveType(lt)}
                                                style={{ padding: "5px 11px", borderRadius: 8, background: "rgba(99,102,241,0.1)", color: "#818cf8", border: "1px solid rgba(99,102,241,0.2)" }}>
                                                <Edit size={13} /> Edit
                                            </ToggleButton>
                                        </Td>
                                    </Tr>
                                ))}
                                {leaveTypes.length === 0 && (
                                    <Tr>
                                        <Td colSpan={4} style={{ textAlign: "center", padding: 32, color: "#64748b" }}>
                                            No leave types configured yet.
                                        </Td>
                                    </Tr>
                                )}
                            </tbody>
                        </Table>
                    </TableScrollWrapper>
                </Card>
            )}

            {activeTab === "roster" && (
                <DutyRoster
                    shifts={shifts}
                    departments={departments}
                    globalDepartments={globalDepartments}
                    role={role}
                    isRestricted={isRestricted}
                    userDept={userDept}
                />
            )}
        </Container>
    );
};

export default ShiftManagement;
